import {
  Image as AntImage,
  message,
  Button,
  Drawer,
  Input,
  Select,
  Divider,
} from "antd";
import {
  LoadingOutlined,
  PictureOutlined,
  YoutubeOutlined,
  EditOutlined,
  CloseOutlined,
  RightOutlined,
  LeftOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { remove } from "ramda";

import config from "@config";
import * as API from "@api";

export default function Widget({ input, onChange, key, data }) {
  const [loading, setLoading] = useState(false);
  const [media, setMedia] = useState(data?.data?.[input.key] || []);
  const [current, setCurrent] = useState(null);

  const { getRootProps, getInputProps } = useDropzone({
    disabled: loading,
    multiple: false,
    accept: {
      "image/png": [".png"],
      "image/jpg": [".jpeg", ".jpg"],
    },
    onDrop: async (file) => {
      if (Array.isArray(file)) {
        file = file[0];
      }

      if (file.size > 10 * 1000 * 1000) {
        return message.error("Файл не должен превышать 10 мегабайт");
      }

      if (file.type.includes("image")) {
        try {
          setLoading(true);
          const _res = await API.Static.uploadImage({
            file,
          });

          if (!_res || !_res?.name) {
            throw new Error();
          }

          setLoading(false);
          setMedia([
            ...media,
            {
              url: _res?.name,
              type: "image",
              alt: "",
              size: "large",
              textalign: "left",
            },
          ]);
          return message.success("Image uploaded");
        } catch (error) {
          setLoading(false);
          return message.error(
            `Could not upload image. ${
              error?.message || error?.response?.data
            }`,
            2
          );
        }
      }
    },
  });

  useEffect(() => {
    onChange(input.key, media);
  }, [media]);

  const onPositionChange = (idx, direction) => {
    if (direction === "left") {
      if (idx === 0) {
        return;
      }

      const _media = [...media];
      const _item = _media[idx];
      _media[idx] = _media[idx - 1];
      _media[idx - 1] = _item;

      setMedia(_media);
    }

    if (direction === "right") {
      if (idx === media.length - 1) {
        return;
      }

      const _media = [...media];
      const _item = _media[idx];
      _media[idx] = _media[idx + 1];
      _media[idx + 1] = _item;

      setMedia(_media);
    }
  };

  const onMediaDelete = (idx) => {
    setMedia(remove(idx, 1, media));
  };

  const getMedia = (item, idx) => {
    if (item?.type !== "video" && item?.type !== "image") {
      return null;
    }

    return (
      <div key={idx} className="col-span-12 md:col-span-6">
        {item?.type === "image" && (
          <AntImage
            width="100%"
            height={150}
            src={`${config.api}/static/img/${item.url}`}
            className="object-cover object-center block"
          />
        )}
        <Button.Group className="w-full">
          <Button
            size="small"
            type="text"
            disabled={idx === 0}
            className="w-full"
            icon={<LeftOutlined />}
            onClick={() => onPositionChange(idx, "left")}
          />
          <Button
            size="small"
            type="text"
            className="w-full"
            icon={<EditOutlined />}
            onClick={() => setCurrent(idx)}
          />
          <Button
            size="small"
            type="text"
            className="w-full"
            onClick={() => onMediaDelete(idx)}
            danger
            icon={<CloseOutlined />}
          />
          <Button
            size="small"
            type="text"
            disabled={idx === media.length - 1}
            className="w-full"
            icon={<RightOutlined />}
            onClick={() => onPositionChange(idx, "right")}
          />
        </Button.Group>
      </div>
    );
  };

  const getDrawerContent = () => {
    if (current === null) {
      return null;
    }

    const item = media[current];

    return (
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <span className="block text-sm font-medium text-neutral-800 mb-2">
            Title
          </span>
          <Input
            value={item?.title}
            onChange={(e) => {
              const _media = [...media];
              _media[current].title = e.target.value;
              setMedia(_media);
            }}
            placeholder="Enter title for this slide"
          />
        </div>
        <div className="col-span-12">
          <span className="block text-sm font-medium text-neutral-800 mb-2">
            Text
          </span>
          <Input.TextArea
            value={item?.text}
            onChange={(e) => {
              const _media = [...media];
              _media[current].text = e.target.value;
              setMedia(_media);
            }}
            placeholder="Enter text for this slide"
          />
        </div>
        <div className="col-span-12">
          <span className="block text-sm font-medium text-neutral-800 mb-2">
            Button (URL)
          </span>
          <Input
            value={item?.href}
            onChange={(e) => {
              const _media = [...media];
              _media[current].href = e.target.value;
              setMedia(_media);
            }}
            placeholder="Enter button URL"
            prefix={<LinkOutlined />}
          />
        </div>
        <div className="col-span-12">
          <span className="block text-sm font-medium text-neutral-800 mb-2">
            Button Label
          </span>
          <Input
            value={item?.label}
            onChange={(e) => {
              const _media = [...media];
              _media[current].label = e.target.value;
              setMedia(_media);
            }}
            placeholder="Enter button label"
          />
        </div>
        <div className="col-span-12">
          <span className="block text-sm font-medium text-neutral-800 mb-2">
            Open in new tab?
          </span>
          <Select
            className="w-full"
            placeholder="Select an option"
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
            onChange={(value) => {
              const _media = [...media];
              _media[current].href_target_blank = value;
              setMedia(_media);
            }}
            defaultValue={false}
            value={item?.href_target_blank}
          />
        </div>
        <div className="col-span-12">
          <span className="block text-sm font-medium text-neutral-800 mb-2">
            Link rel tag
          </span>
          <Input
            value={item?.href_rel}
            onChange={(e) => {
              const _media = [...media];
              _media[current].href_rel = e.target.value;
              setMedia(_media);
            }}
            placeholder="Enter rel value (eg. noopenner noreferrer)"
            prefix={<LinkOutlined />}
          />
        </div>
        <div className="col-span-12">
          <Divider className="my-2" />
        </div>
        <div className="col-span-12">
          <span className="block text-sm font-medium text-neutral-800 mb-2">
            Layout Option (How to display this slide)
          </span>
          <Select
            className="w-full"
            placeholder="Select an option"
            options={[
              { label: "Full-width slide", value: "large" },
              {
                label: "Container-width slide (max-width: 1200px)",
                value: "medium",
              },
              {
                label: "Compact slide",
                value: "compact",
              },
            ]}
            onChange={(value) => {
              const _media = [...media];
              _media[current].size = value;
              setMedia(_media);
            }}
            defaultValue="large"
            value={item?.size}
          />
        </div>
        <div className="col-span-12">
          <span className="block text-sm font-medium text-neutral-800 mb-2">
            Text align
          </span>
          <Select
            className="w-full"
            placeholder="Select an option"
            options={[
              { label: "Left", value: "left" },
              {
                label: "Center",
                value: "center",
              },
              {
                label: "Right",
                value: "right",
              },
            ]}
            onChange={(value) => {
              const _media = [...media];
              _media[current].textalign = value;
              setMedia(_media);
            }}
            defaultValue="left"
            value={item?.textalign}
          />
        </div>
        <div className="col-span-12">
          <Button
            className="mt-5"
            type="primary"
            block
            onClick={() => setCurrent(null)}
          >
            Done
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="col-span-12">
      <span className="block text-xs font-medium text-neutral-800 mb-2">
        Slider {input.required && <i className="not-italic text-rose-600">*</i>}
      </span>

      <div className="grid grid-cols-12 gap-2">
        {media?.map((item, idx) => getMedia(item, idx))}
        <div hidden={media?.length >= 20} className="col-span-12 md:col-span-6">
          <div
            className="ant-upload ant-upload-drag cursor-pointer hover:border-primary transition-long hover:transition-long bg-neutral-50 border-dashed border border-neutral-300 rounded-md m-0 py-5"
            {...getRootProps()}
          >
            <input
              multiple={false}
              {...getInputProps()}
              accept=".png, .jpg, .jpeg"
            />
            <p className="mx-auto block w-fit text-3xl mb-3 text-primary">
              {loading ? (
                <LoadingOutlined />
              ) : (
                <span className="flex gap-2">
                  <PictureOutlined />
                  <YoutubeOutlined />
                </span>
              )}
            </p>
            <p className="text-sm text-black w-full block text-center mb-0">
              Add slide
            </p>
            <p className="text-xs text-neutral-500 w-full block text-center m-0">
              Max image size 10MB
            </p>
          </div>
        </div>
      </div>
      <Drawer
        placement="right"
        open={!!current || current === 0}
        onClose={() => setCurrent(null)}
        title="Slide info"
      >
        {getDrawerContent()}
      </Drawer>
    </div>
  );
}
